import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchReservationByToken,
  updateReservation,
  deleteReservation,
} from "../../api/reservationApi";
import {
  fetchAteliers,
  fetchDisponibiliteJour,
  fetchDisponibiliteMois,
  clearDisponibiliteMois,
} from "../../redux/reservationSlice";
import Calendar from "../../components/calendar/Calendar";
import ReservationSelector from "../../components/reservationSelector/ReservationSelector";
import { ICONS } from "../../utils/fontawesome";

const EMPTY_FORM = {
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  participants: "",
  commentaire: "",
  atelierNom: "",
  dateAtelier: "",
  heureDebut: "",
  heureFin: "",
};

const formatDateLong = (value) => {
  if (!value) return "Date en attente de confirmation";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const SUPPORT_EMAIL = "contact@mood-cafe-atelier.com";
const LINK_ERROR_MESSAGE = `Votre lien de modification ne fonctionne pas, contactez l'équipe Mood à ${SUPPORT_EMAIL}.`;

const renderErrorText = (message) => {
  if (message === LINK_ERROR_MESSAGE) {
    const [before = "", after = ""] = message.split(SUPPORT_EMAIL);
    return (
      <>
        {before}
        <span className="underline font-semibold">{SUPPORT_EMAIL}</span>
        {after}
      </>
    );
  }
  return message;
};

const formatHoraire = (start, end) => {
  const normalize = (time) => {
    if (!time) return null;
    if (/^\d{2}:\d{2}$/.test(time)) return `${time}:00`;
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time;
    return time;
  };
  const toDisplay = (time) => {
    if (!time) return null;
    const [h = "00", m = "00"] = time.split(":");
    return `${h.padStart(2, "0")}h${m.padStart(2, "0")}`;
  };
  const startDisplay = toDisplay(normalize(start));
  const endDisplay = toDisplay(normalize(end));
  if (!startDisplay && !endDisplay) return "Horaire disponible prochainement";
  if (startDisplay && !endDisplay) return `${startDisplay}`;
  if (!startDisplay && endDisplay) return endDisplay;
  return `${startDisplay} - ${endDisplay}`;
};

const toInputString = (value) => {
  if (value === null || value === undefined) return "";
  return typeof value === "string" ? value : String(value);
};

const normalizeDateForApi = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const extractHourNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const str = String(value).trim();
  const match = str.match(/^(\d{1,2})/);
  if (!match) return null;
  return Number(match[1]);
};

const toDateInstance = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const areDatesEqual = (first, second) => {
  if (!first && !second) return true;
  const dateA = toDateInstance(first);
  const dateB = toDateInstance(second);
  if (!dateA || !dateB) return false;
  return dateA.getTime() === dateB.getTime();
};

const normalizeTimeValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    const hour = String(Math.max(0, Math.min(23, Math.floor(value)))).padStart(
      2,
      "0"
    );
    return `${hour}:00:00`;
  }
  if (typeof value === "string") {
    if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
    if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    if (/^\d{1,2}$/.test(value)) {
      const padded = String(value).padStart(2, "0");
      return `${padded}:00:00`;
    }
  }
  return null;
};

const addHoursToTime = (timeStr, hoursToAdd = 0) => {
  if (!timeStr) return null;
  const base = new Date(`1970-01-01T${timeStr}`);
  if (Number.isNaN(base.getTime())) return null;
  base.setHours(base.getHours() + Number(hoursToAdd || 0));
  return base.toTimeString().slice(0, 8);
};

const mapReservationToForm = (reservation) => ({
  nom: toInputString(reservation?.client_data?.nom),
  prenom: toInputString(reservation?.client_data?.prenom),
  email: toInputString(reservation?.client_data?.email),
  telephone: toInputString(reservation?.client_data?.telephone),
  participants: toInputString(reservation?.nombre_inscrit),
  commentaire: toInputString(reservation?.commentaire),
  atelierNom: toInputString(reservation?.atelier),
  dateAtelier: toInputString(reservation?.date_atelier),
  heureDebut: toInputString(reservation?.creneau_heure_debut),
  heureFin: toInputString(reservation?.creneau_heure_fin),
});

const EDITABLE_FIELDS = [
  "nom",
  "prenom",
  "telephone",
  "participants",
  "commentaire",
];

const getLinkParamsFromLocation = () => {
  if (typeof window === "undefined" || !window?.location) {
    return { creneauId: "", token: "" };
  }

  try {
    const currentUrl = new URL(window.location.href);
    const creneauMatch = currentUrl.pathname.match(
      /\/reservation\/link\/([^/]+)/i
    );
    return {
      creneauId: creneauMatch?.[1] ?? "",
      token: currentUrl.searchParams.get("token") ?? "",
    };
  } catch {
    return { creneauId: "", token: "" };
  }
};

function ManageReservation() {
  const { creneauId, token } = useMemo(getLinkParamsFromLocation, []);
  const dispatch = useDispatch();
  const ateliers = useSelector((state) => state.reservation.ateliers || []);
  const disponibiliteJour = useSelector(
    (state) => state.reservation.creneauxJour || []
  );
  const disponibiliteMois = useSelector(
    (state) => state.reservation.creneaux || []
  );

  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [initialValues, setInitialValues] = useState(EMPTY_FORM);
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitMessageType, setSubmitMessageType] = useState("success");
  const [reservationDeleted, setReservationDeleted] = useState(false);
  const [scheduleSelection, setScheduleSelection] = useState({
    date: null,
    atelierId: "",
    participants: 1,
    creneau: null,
    creneauId: null,
  });
  const [plannerDate, setPlannerDate] = useState(null);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [hasInitializedSchedule, setHasInitializedSchedule] = useState(false);

  useEffect(() => {
    dispatch(fetchAteliers());
  }, [dispatch]);

  useEffect(() => {
    if (!creneauId || !token) {
      setLoading(false);
      setError(LINK_ERROR_MESSAGE);
      return;
    }

    let isMounted = true;
    const loadReservation = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchReservationByToken(creneauId, token);
        if (!isMounted) return;
        const mapped = mapReservationToForm(data);
        setReservation(data);
        setFormValues(mapped);
        setInitialValues(mapped);
        setReservationDeleted(false);
        setSubmitMessage("");
        setSubmitMessageType("success");
      } catch (err) {
        if (!isMounted) return;
        setError(LINK_ERROR_MESSAGE);
        setReservation(null);
        setFormValues(EMPTY_FORM);
        setInitialValues(EMPTY_FORM);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadReservation();
    return () => {
      isMounted = false;
    };
  }, [creneauId, token, reloadKey]);


  const isFieldEdited = (field) => {
    const current = formValues?.[field] ?? "";
    const initial = initialValues?.[field] ?? "";
    return current !== initial;
  };

  const isReservationCancelled = useMemo(() => {
    const statusValue = String(reservation?.status || "").toUpperCase();
    return statusValue === "ANNULE" || reservationDeleted;
  }, [reservation, reservationDeleted]);

  const isFormReadOnly = isReservationCancelled;

  const scheduleHasChanged = useMemo(() => {
    const initialDate = initialValues?.dateAtelier
      ? normalizeDateForApi(initialValues.dateAtelier)
      : null;
    const currentDate = scheduleSelection.date
      ? normalizeDateForApi(scheduleSelection.date)
      : formValues.dateAtelier || null;
    const dateChanged =
      initialDate && currentDate
        ? initialDate !== currentDate
        : !!(initialDate || currentDate);

    const initialAtelier =
      reservation?.atelier_id ||
      reservation?.atelierId ||
      reservation?.atelier_uuid ||
      "";
    const currentAtelier = scheduleSelection.atelierId || initialAtelier;
    const atelierChanged =
      Boolean(initialAtelier) && Boolean(currentAtelier)
        ? String(initialAtelier) !== String(currentAtelier)
        : Boolean(initialAtelier) !== Boolean(currentAtelier);

    return dateChanged || atelierChanged;
  }, [
    initialValues?.dateAtelier,
    scheduleSelection.date,
    formValues.dateAtelier,
    scheduleSelection.atelierId,
    reservation?.atelier_id,
    reservation?.atelierId,
    reservation?.atelier_uuid,
  ]);

  const hasFormChanges = useMemo(() => {
    if (isFormReadOnly) return false;
    const baseChanges = EDITABLE_FIELDS.some((field) => isFieldEdited(field));
    return baseChanges || scheduleHasChanged;
  }, [formValues, initialValues, isFormReadOnly, scheduleHasChanged]);

  const getFieldHighlightStyle = (field) =>
    isFieldEdited(field) ? { backgroundColor: "#F7BFD8" } : undefined;

  const resolvedAtelierId = useMemo(() => {
    if (reservation?.atelier_id) return reservation.atelier_id;
    if (reservation?.atelierId) return reservation.atelierId;
    if (reservation?.atelier_uuid) return reservation.atelier_uuid;

    const atelierName =
      reservation?.atelier || reservation?.atelier_nom || formValues.atelierNom;
    if (!atelierName) return "";

    const normalizedTarget = atelierName.trim().toLowerCase();
    const matchingAtelier =
      ateliers.find(
        (atelier) =>
          (atelier?.nom || "").trim().toLowerCase() === normalizedTarget
      ) || null;

    return (
      matchingAtelier?.atelier_id ||
      matchingAtelier?.id ||
      matchingAtelier?._id ||
      ""
    );
  }, [reservation, formValues.atelierNom, ateliers]);

  const getAtelierDuration = useCallback(
    (atelierId) => {
      if (!atelierId) return null;

      const match =
        ateliers.find(
          (atelier) =>
            String(atelier?.atelier_id ?? atelier?.id) === String(atelierId)
        ) || null;
      const duration = Number(match?.duree);
      return Number.isFinite(duration) && duration > 0 ? duration : null;
    },
    [ateliers]
  );

  useEffect(() => {
    if (!reservation || hasInitializedSchedule) return;

    const deriveDate = () => {
      const source =
        reservation?.date_atelier ||
        formValues?.dateAtelier ||
        reservation?.date ||
        null;
      if (!source) return new Date();
      const dateObj = source instanceof Date ? source : new Date(source);
      if (Number.isNaN(dateObj.getTime())) return new Date();
      return dateObj;
    };

    const initialDate = deriveDate();
    const initialParticipants =
      Number(reservation?.nombre_inscrit) ||
      Number(formValues?.participants) ||
      1;
    const initialCreneau =
      reservation?.creneau ||
      (reservation
        ? {
            creneau_heure_debut:
              reservation?.creneau_heure_debut ??
              reservation?.creneau?.heure_debut ??
              reservation?.creneau?.heure ??
              null,
            creneau_heure_fin:
              reservation?.creneau_heure_fin ??
              reservation?.creneau?.heure_fin ??
              reservation?.creneau?.fin ??
              null,
          }
        : null);
    const initialCreneauId =
      reservation?.creneau_id ??
      reservation?.creneau?.creneau_id ??
      reservation?.creneau?.id ??
      reservation?.id ??
      null;

    setScheduleSelection({
      date: initialDate,
      atelierId: resolvedAtelierId || "",
      participants: initialParticipants,
      creneau: initialCreneau,
      creneauId: initialCreneauId,
    });
    setPlannerDate(initialDate);
    setHasInitializedSchedule(true);
  }, [
    reservation,
    resolvedAtelierId,
    hasInitializedSchedule,
    formValues?.dateAtelier,
    formValues?.participants,
  ]);

  const refreshDisponibiliteJour = (atelierId, dateValue) => {
    const dateKey = normalizeDateForApi(dateValue);
    if (!atelierId || !dateKey) return;
    dispatch(
      fetchDisponibiliteJour({
        date: dateKey,
        atelier: atelierId,
      })
    );
  };

  useEffect(() => {
    const targetAtelier = scheduleSelection.atelierId || resolvedAtelierId;
    const targetDate =
      scheduleSelection.date ||
      (formValues.dateAtelier ? new Date(formValues.dateAtelier) : null);
    if (!targetAtelier || !targetDate) return;
    refreshDisponibiliteJour(targetAtelier, targetDate);
  }, [
    dispatch,
    resolvedAtelierId,
    scheduleSelection.atelierId,
    scheduleSelection.date,
    formValues.dateAtelier,
  ]);

  useEffect(() => {
    const targetAtelier = scheduleSelection.atelierId || resolvedAtelierId;
    const baseDate = plannerDate || scheduleSelection.date;
    if (!targetAtelier || !baseDate) return;
    const dateObj = baseDate instanceof Date ? baseDate : new Date(baseDate);
    if (Number.isNaN(dateObj.getTime())) return;
    dispatch(
      fetchDisponibiliteMois({
        annee: dateObj.getFullYear(),
        mois: dateObj.getMonth() + 1,
        atelier: targetAtelier,
      })
    );
  }, [
    dispatch,
    plannerDate,
    scheduleSelection.atelierId,
    scheduleSelection.date,
    resolvedAtelierId,
  ]);

  const handlePlannerMonthChange = useCallback(
    ({ year, month }) => {
      const targetAtelier = scheduleSelection.atelierId || resolvedAtelierId;
      if (!targetAtelier || !year || !month) return;
      dispatch(clearDisponibiliteMois());
      dispatch(
        fetchDisponibiliteMois({
          annee: year,
          mois: month,
          atelier: targetAtelier,
        })
      );
    },
    [dispatch, scheduleSelection.atelierId, resolvedAtelierId]
  );

  const handleRetry = () => setReloadKey((prev) => prev + 1);

  const placesRestantes = useMemo(() => {
    if (!Array.isArray(disponibiliteJour) || disponibiliteJour.length === 0) {
      return null;
    }

    const reservationHourSource =
      scheduleSelection?.creneau?.creneau_heure_debut ?? formValues.heureDebut;
    const reservationHour = extractHourNumber(reservationHourSource);
    if (reservationHour === null) return null;

    const match = disponibiliteJour.find(
      (item) => extractHourNumber(item?.heure) === reservationHour
    );
    if (!match) return null;

    const selectedDateForComparison =
      scheduleSelection?.date ||
      formValues.dateAtelier ||
      reservation?.date_atelier ||
      null;
    const normalizedSelectedDate =
      normalizeDateForApi(selectedDateForComparison);
    const normalizedReservationDate = normalizeDateForApi(
      reservation?.date_atelier
    );
    const reservedCreneauId =
      reservation?.creneau_id ??  null;

    const selectionMatchesReservation = (() => {
      const selectedId = scheduleSelection?.creneauId;
      if (selectedId && reservedCreneauId) {
        return String(selectedId) === String(reservedCreneauId);
      }

      const reservedHour = extractHourNumber(
        reservation?.creneau_heure_debut
      );
      if (reservedHour === null || reservedHour !== reservationHour) {
        return false;
      }
      if (!normalizedSelectedDate || !normalizedReservationDate) {
        return false;
      }
      return normalizedSelectedDate === normalizedReservationDate;
    })();

    const placesNumber = Number(match.places);
    let availablePlaces = Number.isFinite(placesNumber)
      ? placesNumber
      : null;

    if (selectionMatchesReservation) {
      const capaciteSource =
        match.capacite_max_totale ?? reservation?.capacite_max_totale;
      const inscritsSource =
        match.nombre_inscrit ?? reservation?.nombre_inscrit;
      const capacite = Number(capaciteSource);
      const inscrits = Number(inscritsSource);
      if (Number.isFinite(capacite) && Number.isFinite(inscrits)) {
        const diff = capacite - inscrits;
        if (diff > 0) {
          availablePlaces = (availablePlaces ?? 0) + diff;
        }
      }
    }

    return availablePlaces;
  }, [
    disponibiliteJour,
    scheduleSelection?.creneau,
    scheduleSelection?.creneauId,
    scheduleSelection?.date,
    formValues.heureDebut,
    formValues.dateAtelier,
    reservation?.creneau_id,
    reservation?.creneau?.creneau_id,
    reservation?.creneau?.id,
    reservation?.id,
    reservation?.creneau_heure_debut,
    reservation?.date_atelier,
    reservation?.capacite_max_totale,
    reservation?.nombre_inscrit,
  ]);

  const participantsMax = useMemo(() => {
    const reservedCount = Number(reservation?.nombre_inscrit);
    const base =
      Number.isFinite(reservedCount) && reservedCount > 0 ? reservedCount : 0;
    const extra =
      typeof placesRestantes === "number" && placesRestantes > 0
        ? placesRestantes
        : 0;
    const total = base + extra;
    return total > 0 ? total : undefined;
  }, [reservation, placesRestantes]);

  const handleInputChange = (field) => (event) => {
    if (isFormReadOnly) return;
    let { value } = event.target;
    setSubmitMessage("");

    if (field === "participants") {
      const parsedValue = Number(value);
      if (Number.isFinite(parsedValue)) {
        const sanitized = Math.max(1, Math.floor(parsedValue));
        const limited =
          typeof participantsMax === "number"
            ? Math.min(sanitized, participantsMax)
            : sanitized;
        value = String(limited);
        setScheduleSelection((prev) => ({
          ...prev,
          participants: limited,
        }));
      }
    }

    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenPlanner = () => {
    if (isFormReadOnly) return;
    const fallbackDate =
      scheduleSelection.date ||
      (formValues.dateAtelier ? new Date(formValues.dateAtelier) : null) ||
      new Date();
    setPlannerDate(fallbackDate);
    setIsEditingSchedule(true);
  };

  const handleReturnToForm = () => {
    setIsEditingSchedule(false);
  };
  const handlePlannerSelectionChange = useCallback(
    (selection) => {
      if (!selection || !isEditingSchedule) return;

      const hasCreneauField =
        Object.prototype.hasOwnProperty.call(selection, "creneau");
      const hasCreneauIdField =
        Object.prototype.hasOwnProperty.call(selection, "creneauId");
      const resolvedSelectionAtelierId =
        selection.atelierId ||
        scheduleSelection.atelierId ||
        resolvedAtelierId ||
        "";

      setScheduleSelection((prev) => {
        let nextDate = prev.date;
        if (selection.date) {
          const dateChanged = !areDatesEqual(prev.date, selection.date);
          if (dateChanged) {
            const parsedDate = toDateInstance(selection.date);
            if (parsedDate) {
              nextDate = parsedDate;
            }
          }
        }

        return {
          date: nextDate,
          atelierId: selection.atelierId || prev.atelierId,
          participants: Number(selection.participants) || prev.participants,
          creneau: hasCreneauField ? selection.creneau : prev.creneau,
          creneauId: hasCreneauIdField ? selection.creneauId : prev.creneauId,
        };
      });

      setFormValues((prev) => {
        const updated = { ...prev };
        if (selection.date) {
          updated.dateAtelier = normalizeDateForApi(selection.date);
        }

        if (selection.creneau) {
          const heureDebutValue =
            selection.creneau?.creneau_heure_debut ??
            selection.creneau?.heure_debut ??
            selection.creneau?.heure ??
            updated.heureDebut;
          updated.heureDebut = heureDebutValue;

          const heureFinDirect =
            selection.creneau?.creneau_heure_fin ??
            selection.creneau?.heure_fin ??
            selection.creneau?.fin ??
            null;

          let heureFinValue = heureFinDirect;
          if (!heureFinValue) {
            const normalizedStart = normalizeTimeValue(heureDebutValue);
            const duration = getAtelierDuration(resolvedSelectionAtelierId);
            if (normalizedStart && duration) {
              heureFinValue = addHoursToTime(normalizedStart, duration);
            }
          }
          if (heureFinValue) {
            updated.heureFin = heureFinValue;
          }
        } else if (hasCreneauField && !selection.creneau) {
          updated.heureDebut = "";
          updated.heureFin = "";
        }

        if (selection.atelierNom) {
          updated.atelierNom = selection.atelierNom;
        } else if (selection.atelierId) {
          const match = ateliers.find(
            (a) => String(a.atelier_id ?? a.id) === String(selection.atelierId)
          );
          if (match?.nom) {
            updated.atelierNom = match.nom;
          }
        }
        if (selection.participants) {
          updated.participants = String(selection.participants);
        }
        return updated;
      });
    },
    [
      ateliers,
      getAtelierDuration,
      isEditingSchedule,
      resolvedAtelierId,
      scheduleSelection.atelierId,
    ]
  );

  const buildUpdatePayload = (overrides = {}) => {
    if (!reservation) return null;

    const resolvedAtelier = scheduleSelection.atelierId || resolvedAtelierId;

    const fallbackParticipants = Number(reservation?.nombre_inscrit) || 1;
    const requestedParticipants = Number(formValues.participants);
    const nombreInscrit = Number.isNaN(requestedParticipants)
      ? fallbackParticipants
      : requestedParticipants;

    const selectedDateSource =
      scheduleSelection.date ||
      (formValues.dateAtelier ? new Date(formValues.dateAtelier) : null) ||
      reservation?.date_atelier ||
      null;
    const normalizedDateValue = selectedDateSource
      ? normalizeDateForApi(selectedDateSource)
      : formValues.dateAtelier || reservation?.date_atelier || "";
    const creneauDebut =
      scheduleSelection?.creneau?.creneau_heure_debut ??
      scheduleSelection?.creneau?.heure_debut ??
      scheduleSelection?.creneau?.heure ??
      formValues.heureDebut ??
      reservation?.creneau_heure_debut ??
      "";
    const normalizedCreneauDebut =
      normalizeTimeValue(creneauDebut) || creneauDebut || "";
    let creneauFin =
      scheduleSelection?.creneau?.creneau_heure_fin ??
      scheduleSelection?.creneau?.heure_fin ??
      scheduleSelection?.creneau?.fin ??
      formValues.heureFin ??
      reservation?.creneau_heure_fin ??
      "";
    if (!creneauFin && normalizedCreneauDebut) {
      const duration = getAtelierDuration(resolvedAtelier);
      if (duration) {
        creneauFin = addHoursToTime(normalizedCreneauDebut, duration) || "";
      }
    }

    const basePayload = {
      date_atelier: normalizedDateValue,
      creneau_heure_debut: normalizedCreneauDebut,
      creneau_heure_fin: creneauFin || "",
      nombre_inscrit: nombreInscrit,
      status: reservation?.status || "RESERVE",
      admin_input: Boolean(reservation?.admin_input),
      commentaire: formValues.commentaire || "",
      atelier: resolvedAtelier || "",
      client_data: {
        nom: formValues.nom.trim(),
        prenom: formValues.prenom.trim(),
        email: formValues.email.trim(),
        telephone: formValues.telephone.trim(),
      },
    };

    return { ...basePayload, ...(overrides || {}) };
  };

  const handleSubmitChanges = async () => {
    if (!hasFormChanges || !reservation || isReservationCancelled) return;

    const payload = buildUpdatePayload();
    if (!payload) {
      setSubmitMessage("Informations de réservation incomplètes.");
      setSubmitMessageType("error");
      return;
    }
    if (!payload.atelier) {
      setSubmitMessage(
        "Atelier introuvable pour cette réservation, contactez l'équipe Mood."
      );
      setSubmitMessageType("error");
      return;
    }

    setSubmitMessage("");
    setSubmitMessageType("success");

    try {
      setIsSubmitting(true);
      const updatedReservation = await updateReservation(
        creneauId,
        token,
        payload
      );
      const mapped = mapReservationToForm(updatedReservation);
      setHasInitializedSchedule(false);
      setReservation(updatedReservation);
      setFormValues(mapped);
      setInitialValues(mapped);
      refreshDisponibiliteJour(
        scheduleSelection.atelierId || resolvedAtelierId,
        mapped.dateAtelier || reservation?.date_atelier
      );
      setSubmitMessage("Réservation mise à jour avec succès.");
      setSubmitMessageType("success");
    } catch (err) {
      setSubmitMessage(
        err?.message || "Impossible de mettre à jour la réservation."
      );
      setSubmitMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelReservation = async () => {
    if (!reservation || isCancelling || isReservationCancelled) return;

    setSubmitMessage("");
    setSubmitMessageType("success");

    try {
      setIsCancelling(true);
      await deleteReservation(creneauId, token);
      setReservation((prev) => (prev ? { ...prev, status: "ANNULE" } : prev));
      setReservationDeleted(true);
      setIsEditingSchedule(false);
      refreshDisponibiliteJour(
        resolvedAtelierId,
        formValues.dateAtelier || reservation?.date_atelier
      );
      setSubmitMessage(
        "Votre réservation a été annulée et supprimée. Le créneau est libéré."
      );
      setSubmitMessageType("success");
    } catch (err) {
      setSubmitMessage(
        err?.message || "Impossible d'annuler la réservation."
      );
      setSubmitMessageType("error");
    } finally {
      setIsCancelling(false);
    }
  };

  const dateAffichee = useMemo(() => {
    if (scheduleSelection.date) {
      return formatDateLong(scheduleSelection.date);
    }
    return formatDateLong(formValues.dateAtelier);
  }, [scheduleSelection.date, formValues.dateAtelier]);
  const horaireAffiche = useMemo(() => {
    const heureDebutSource =
      scheduleSelection?.creneau?.creneau_heure_debut ??
      scheduleSelection?.creneau?.heure_debut ??
      scheduleSelection?.creneau?.heure ??
      formValues.heureDebut;
    const heureFinSource =
      scheduleSelection?.creneau?.creneau_heure_fin ??
      scheduleSelection?.creneau?.heure_fin ??
      scheduleSelection?.creneau?.fin ??
      formValues.heureFin;
    return formatHoraire(heureDebutSource, heureFinSource);
  }, [scheduleSelection?.creneau, formValues.heureDebut, formValues.heureFin]);

  const plannerActiveDate = useMemo(() => {
    const baseDate =
      plannerDate ||
      scheduleSelection.date ||
      (formValues.dateAtelier ? new Date(formValues.dateAtelier) : null);
    if (!baseDate) return new Date();
    const dateObj = baseDate instanceof Date ? baseDate : new Date(baseDate);
    if (Number.isNaN(dateObj.getTime())) return new Date();
    return dateObj;
  }, [plannerDate, scheduleSelection.date, formValues.dateAtelier]);

  return (
    <div className="w-full px-4 py-10">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-orange-900 mb-6">
          Gérer ma réservation
        </h1>

        {loading && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-orange-800">
            Chargement des informations de votre réservation...
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <p className="mb-3">{renderErrorText(error)}</p>
            {creneauId && token && (
              <button
                type="button"
                onClick={handleRetry}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
              >
                Réessayer
              </button>
            )}
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-lg shadow-orange-100 border border-orange-100 p-6">
            <div className="grid gap-4 md:grid-cols-[0.6fr_1fr_1fr] mb-8 md:gap-6">
              <div
                className={`rounded-lg p-4 border md:col-span-1 md:max-w-xs ${
                  String(reservation?.status || "").toUpperCase() === "RESERVE"
                    ? "bg-green-50/70 border-green-100 text-green-900"
                    : "bg-orange-50 border-orange-100 text-orange-900"
                }`}
              >
                <p className="text-xs uppercase tracking-wide">
                  Statut actuel
                </p>
                <p className="text-base font-semibold">
                  {reservation?.status || "Inconnu"}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                <p className="text-xs uppercase tracking-wide text-orange-500">
                  Atelier
                </p>
                <p className="text-base text-orange-900 font-semibold">
                  {formValues.atelierNom || "Nom d'atelier non communiqué"}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                <p className="text-xs uppercase tracking-wide text-orange-500">
                  Date & horaire
                </p>
                <p className="text-base text-orange-900 font-semibold">
                  {dateAffichee} — {horaireAffiche}
                </p>
              </div>
            </div>

            <div className="relative mt-6 overflow-hidden">
              <div
                className={`flex w-[200%] transform transition-transform duration-500 ease-in-out ${
                  isEditingSchedule ? "-translate-x-1/2" : "translate-x-0"
                }`}
              >
                <div className="w-full px-0 md:px-2">
                  <form
                    className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                      isFormReadOnly ? "opacity-60" : ""
                    }`}
                  >
                    <div>
                      <label className="block text-sm font-medium text-orange-600 mb-1">
                        Nom
                      </label>
                      <input
                        value={formValues.nom}
                        onChange={handleInputChange("nom")}
                        disabled={isFormReadOnly}
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                          isFormReadOnly
                            ? "border-orange-100 bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "border-orange-200"
                        }`}
                        style={getFieldHighlightStyle("nom")}
                        placeholder="Nom"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-orange-600 mb-1">
                        Prénom
                      </label>
                      <input
                        value={formValues.prenom}
                        onChange={handleInputChange("prenom")}
                        disabled={isFormReadOnly}
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                          isFormReadOnly
                            ? "border-orange-100 bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "border-orange-200"
                        }`}
                        style={getFieldHighlightStyle("prenom")}
                        placeholder="Prénom"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-orange-600 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formValues.email}
                        onChange={handleInputChange("email")}
                        className="w-full border border-orange-100 bg-gray-100 rounded px-3 py-2 text-gray-500 cursor-not-allowed"
                        readOnly
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-orange-600 mb-1">
                        Téléphone
                      </label>
                      <input
                        value={formValues.telephone}
                        onChange={handleInputChange("telephone")}
                        disabled={isFormReadOnly}
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                          isFormReadOnly
                            ? "border-orange-100 bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "border-orange-200"
                        }`}
                        style={getFieldHighlightStyle("telephone")}
                        placeholder="+33 6 00 00 00 00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-orange-600 mb-1">
                        Participants
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={participantsMax ?? undefined}
                        value={formValues.participants}
                        onChange={handleInputChange("participants")}
                        disabled={isFormReadOnly}
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                          isFormReadOnly
                            ? "border-orange-100 bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "border-orange-200"
                        }`}
                        style={getFieldHighlightStyle("participants")}
                      />
                      {typeof placesRestantes === "number" && (
                        <p className="text-xs text-gray-500 mt-1">
                          Places restantes : {Math.max(0, placesRestantes)}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-orange-600 mb-1">
                        Commentaire
                      </label>
                      <textarea
                        value={formValues.commentaire}
                        onChange={handleInputChange("commentaire")}
                        rows={3}
                        disabled={isFormReadOnly}
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                          isFormReadOnly
                            ? "border-orange-100 bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "border-orange-200"
                        }`}
                        style={getFieldHighlightStyle("commentaire")}
                        placeholder="Ajoutez un message pour l'équipe"
                      />
                    </div>
                  </form>

                  <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <p className="text-sm text-gray-500 md:max-w-sm">
                      Modifiez vos informations ci-dessus. Les champs ajustés
                      apparaissent surlignés en rose.
                    </p>
              <div className="flex flex-wrap gap-3 md:items-center">
                  <button
                    type="button"
                    onClick={handleOpenPlanner}
                    disabled={isFormReadOnly}
                    className="md:w-auto px-4 py-2 min-w-[180px] text-sm rounded-lg border text-pink-500 border-pink-500 hover:bg-pink-500 hover:text-white transition inline-flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={ICONS.calendar} className="h-4 w-4" />
                    Modifier la date
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitChanges}
                    disabled={
                      !hasFormChanges || isSubmitting || isReservationCancelled
                    }
                    className={`px-4 py-2 min-w-[180px] rounded-lg text-sm text-white transition inline-flex items-center justify-center gap-2 ${
                      !hasFormChanges || isSubmitting || isReservationCancelled
                        ? "bg-orange-300 text-orange-900/70 cursor-not-allowed"
                        : "bg-orange-600 hover:bg-orange-700"
                    }`}
                  >
                    <FontAwesomeIcon icon={ICONS.thumbsUp} className="h-4 w-4" />
                    {isSubmitting ? "Mise à jour..." : "Enregistrer mes modifications"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelReservation}
                    disabled={isCancelling || isReservationCancelled}
                    className={`px-4 py-2 min-w-[180px] rounded-lg text-sm text-white transition inline-flex items-center justify-center gap-2 ${
                      isCancelling || isReservationCancelled
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-red-800 hover:bg-red-900"
                    }`}
                  >
                    <FontAwesomeIcon icon={ICONS.trash} className="h-4 w-4" />
                    {isCancelling ? "Annulation..." : "Annuler la réservation"}
                  </button>
                    </div>
                  </div>

                  {submitMessage && (
                    <div
                      className={`mt-4 text-sm ${
                        submitMessageType === "success"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {submitMessage}
                    </div>
                  )}
                </div>

                <div className="w-full px-0 md:px-2">
                  <div className="bg-white rounded-xl border border-orange-100 p-4 md:p-6 h-full flex flex-col gap-6">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="text-xl font-semibold text-orange-900">
                        Modifier la date et l'atelier
                      </h3>
                    </div>
                    <div className="grid gap-6 lg:grid-cols-2">
                      <Calendar
                        dateSelectionnee={plannerActiveDate}
                        setDateSelectionne={setPlannerDate}
                        disponibilite={disponibiliteMois}
                        onMonthChange={handlePlannerMonthChange}
                      />
                      <ReservationSelector
                        selectedDate={plannerActiveDate}
                        onSelectionChange={handlePlannerSelectionChange}
                        initialAtelierId={scheduleSelection.atelierId || resolvedAtelierId}
                        initialParticipants={
                          scheduleSelection.participants ||
                          Number(formValues.participants) ||
                          Number(reservation?.nombre_inscrit) ||
                          1
                        }
                        initialCreneauId={scheduleSelection.creneauId}
                        initialCreneau={scheduleSelection.creneau}
                      />
                      
                      <button
                        type="button"
                        onClick={handleReturnToForm}
                        className="px-4 py-2 rounded-lg border text-orange-600 border-orange-600 hover:bg-orange-700 hover:text-white "
                      >
                        Retour au formulaire
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ManageReservation;
